/**
 * GEO SEO Component
 * 
 * Provides geographic-specific SEO optimizations for local search visibility.
 * Targets Chennai, Tamil Nadu, India primarily with international reach.
 */

import { useEffect } from 'react'

interface GeoSEOProps {
  primaryCity?: string
  primaryRegion?: string
  primaryCountry?: string
  serviceAreas?: Array<{
    city: string
    region: string
    country: string
    latitude?: number
    longitude?: number
  }>
}

const DEFAULT_SERVICE_AREAS = [
  { city: 'Chennai', region: 'Tamil Nadu', country: 'India', latitude: 13.0827, longitude: 80.2707 },
  { city: 'Coimbatore', region: 'Tamil Nadu', country: 'India', latitude: 11.0168, longitude: 76.9558 },
  { city: 'Madurai', region: 'Tamil Nadu', country: 'India', latitude: 9.9252, longitude: 78.1198 },
  { city: 'Bangalore', region: 'Karnataka', country: 'India', latitude: 12.9716, longitude: 77.5946 },
  { city: 'Hyderabad', region: 'Telangana', country: 'India', latitude: 17.3850, longitude: 78.4867 },
  { city: 'Mumbai', region: 'Maharashtra', country: 'India', latitude: 19.0760, longitude: 72.8777 },
  { city: 'Dubai', region: 'Dubai', country: 'UAE', latitude: 25.2048, longitude: 55.2708 },
  { city: 'Singapore', region: 'Singapore', country: 'Singapore', latitude: 1.3521, longitude: 103.8198 },
]

export default function GeoSEO({
  primaryCity = 'Chennai',
  primaryRegion = 'Tamil Nadu',
  primaryCountry = 'India',
  serviceAreas = DEFAULT_SERVICE_AREAS
}: GeoSEOProps) {
  
  useEffect(() => {
    // Add LocalBusiness schema with multiple locations
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://sudhasacademy.com/#localbusiness",
      "name": "Sudha's Academy",
      "image": "https://sudhasacademy.com/Hero.png",
      "url": "https://sudhasacademy.com",
      "description": "India's premier beauty and makeup training institute offering professional courses in bridal makeup, skincare, hair styling, and cosmetology since 2006.",
      "priceRange": "₹₹",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": primaryCity,
        "addressLocality": primaryCity,
        "addressRegion": primaryRegion,
        "addressCountry": primaryCountry === 'India' ? 'IN' : primaryCountry
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": serviceAreas.find(a => a.city === primaryCity)?.latitude || 13.0827,
        "longitude": serviceAreas.find(a => a.city === primaryCity)?.longitude || 80.2707
      },
      "areaServed": serviceAreas.map(area => ({
        "@type": "City",
        "name": area.city,
        "containedInPlace": {
          "@type": "AdministrativeArea",
          "name": area.region
        }
      })),
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Beauty Training Courses",
        "itemListElement": [
          {
            "@type": "OfferCatalog",
            "name": "Makeup Courses",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Professional Bridal Makeup Course"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Advanced Makeup Artistry"
                }
              }
            ]
          },
          {
            "@type": "OfferCatalog",
            "name": "Skincare Courses",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Skincare & Facial Treatment"
                }
              }
            ]
          },
          {
            "@type": "OfferCatalog",
            "name": "Hair Styling Courses",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Hair Styling & Cutting Course"
                }
              }
            ]
          }
        ]
      },
      "sameAs": [
        "https://www.facebook.com/sudhasacademy",
        "https://www.instagram.com/sudhasacademy",
        "https://www.youtube.com/sudhasacademy",
        "https://www.linkedin.com/company/sudhasacademy"
      ]
    }

    // Add GeoCircle for service area
    const geoCircleSchema = {
      "@context": "https://schema.org",
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 13.0827,
        "longitude": 80.2707
      },
      "geoRadius": "500000"  // 500km radius for online services
    }

    // Add Place schema for each service location
    const serviceAreasSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Sudha's Academy Service Areas",
      "itemListElement": serviceAreas.map((area, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Place",
          "name": `${area.city}, ${area.region}`,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": area.city,
            "addressRegion": area.region,
            "addressCountry": area.country
          },
          "geo": area.latitude && area.longitude ? {
            "@type": "GeoCoordinates",
            "latitude": area.latitude,
            "longitude": area.longitude
          } : undefined
        }
      }))
    }

    // Add scripts to head
    const addSchema = (id: string, data: object) => {
      const existing = document.getElementById(id)
      if (existing) existing.remove()
      
      const script = document.createElement('script')
      script.id = id
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(data)
      document.head.appendChild(script)
    }

    addSchema('local-business-schema', localBusinessSchema)
    addSchema('geo-circle-schema', geoCircleSchema)
    addSchema('service-areas-schema', serviceAreasSchema)

    return () => {
      ['local-business-schema', 'geo-circle-schema', 'service-areas-schema'].forEach(id => {
        const el = document.getElementById(id)
        if (el) el.remove()
      })
    }
  }, [primaryCity, primaryRegion, primaryCountry, serviceAreas])

  return (
    <>
      {/* Hidden geo-targeting content for search engines */}
      <div className="sr-only" aria-label="Service locations">
        <h2>Sudha's Academy - Serving Beauty Professionals Across India and Internationally</h2>
        
        <p>
          <strong>Headquartered in {primaryCity}, {primaryRegion}, {primaryCountry}</strong>
        </p>
        
        <section aria-label="Cities we serve">
          <h3>Beauty Training Available In:</h3>
          <ul>
            {serviceAreas.map((area, index) => (
              <li key={index}>
                <strong>{area.city}</strong>, {area.region}, {area.country}
                {' - '}
                Makeup courses, bridal makeup training, skincare certification, hair styling classes
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="Local keywords">
          <h3>Find Us With:</h3>
          <ul>
            <li>Best makeup academy in Chennai</li>
            <li>Bridal makeup course Chennai Tamil Nadu</li>
            <li>Professional beauty training institute near me</li>
            <li>Cosmetology diploma Chennai</li>
            <li>Hair styling classes in Tamil Nadu</li>
            <li>Online makeup course India</li>
            <li>Beauty school Chennai</li>
            <li>Makeup artist certification course</li>
            <li>Skincare training Chennai</li>
            <li>Nail art course Tamil Nadu</li>
          </ul>
        </section>
      </div>
    </>
  )
}

// Export service areas for use elsewhere
export { DEFAULT_SERVICE_AREAS }
